import { appstr } from "../utils/appstr";
import {
  enumBoxStyleSearch,
  enumBoxStyleSet,
  enumBrand,
  enumContainerType,
  enumFeaturesList,
  enumHierarchyTypes,
  kStartPUID,
  kStartUID,
  kIsolvedBoxStyleIDs
} from "../constants";
import { gWhiteLabel } from "../whitelabel/brand";
import { isInViewer } from "../utils/isInViewer";
import { QBoxLayout } from "./QBoxLayout";
import { Globals } from "./Globals";
import { isInStandardSubmode } from "../utils/isInStandardSubmode";

export class QBoxStylesManager {
  constructor() {
    this.fSysBoxStyles = null;
    this.fUserBoxStyles = null;
    this.fSharedBoxStyles = null;

    this.fBoxStyleSet = null;
    this.fBoxStyleSet = enumBoxStyleSet.sharedboxstyles;
    this.fHierarchyType = enumHierarchyTypes.employees;

    this.fDirty = null;

    this.fPendingScrollTarget = null;

    this.fUID = kStartUID;
    this.fPUID = kStartPUID;

    this.fEditDrafts = {};
    // Staged deletes of persisted styles: id -> { layout, setId }. Holds the removed layout
    // so discard/revert can re-insert it; commit persists the deletion (whole-list save).
    this.fTombstones = {};
    this.fWSMgr = null;
  }

  SetWSMgr(wsMgr) {
    this.fWSMgr = wsMgr;
  }

  NotifyPendingEditsChanged() {
    this.fWSMgr?.BroadcastTopic?.("boxStylesPendingEditsChanged");
  }

  NotifyActiveDraftMutated() {
    this.fWSMgr?.BroadcastTopic?.("boxStyleDraftMutated");
  }

  // Stores `layout` by reference. Caller must Clone first if the live ref will
  // be destroyed (e.g. NukePanel); only SnapshotForSameModeBLESwitch does this today.
  SetEditDraft(id, layout, { isNew = false } = {}) {
    if (id == null) return;
    var existing = this.fEditDrafts[id];
    var isNewFlag = existing ? existing.isNew || isNew : isNew;
    this.fEditDrafts[id] = { layout, isNew: isNewFlag };
  }

  // Use for user-gesture staging (rename, copy, new style). Use SetEditDraft directly for internal
  // model updates (snapshot refresh, mutation tracking, pre-commit sync).
  StageEditDraft(id, layout, opts) {
    this.SetEditDraft(id, layout, opts);
    this.SetPendingScrollTarget(id);
    this.NotifyPendingEditsChanged();
  }

  GetEditDraft(id) {
    return this.fEditDrafts[id] ?? null;
  }

  HasEditDraft(id) {
    return this.fEditDrafts[id] != null;
  }

  GetAllEditDrafts() {
    // Keys are numeric ids string-coerced by JS object semantics; normalize back to number.
    return Object.entries(this.fEditDrafts).map(([key, entry]) => ({
      id: Number(key),
      layout: entry.layout,
      isNew: entry.isNew
    }));
  }

  ClearAllEditDrafts() {
    this.fEditDrafts = {};
  }

  ClearEditDraft(id) {
    if (id == null) return;
    delete this.fEditDrafts[id];
  }

  HasAnyEditDrafts() {
    return Object.keys(this.fEditDrafts).length > 0;
  }

  GetEditDraftCount() {
    return Object.keys(this.fEditDrafts).length;
  }

  HasNewDraftId(id) {
    return this.fEditDrafts[id]?.isNew === true;
  }

  HasAnyNewDrafts() {
    return Object.values(this.fEditDrafts).some(d => d.isNew);
  }

  AddTombstone(id, layout, setId) {
    if (id == null) return;
    this.fTombstones[id] = { layout, setId };
  }

  GetTombstone(id) {
    return this.fTombstones[id] ?? null;
  }

  HasTombstone(id) {
    return this.fTombstones[id] != null;
  }

  GetAllTombstones() {
    return Object.entries(this.fTombstones).map(([key, entry]) => ({
      id: Number(key),
      layout: entry.layout,
      setId: entry.setId
    }));
  }

  ClearTombstone(id) {
    if (id == null) return;
    delete this.fTombstones[id];
  }

  ClearAllTombstones() {
    this.fTombstones = {};
  }

  HasAnyTombstones() {
    return Object.keys(this.fTombstones).length > 0;
  }

  HasAnyPendingChanges() {
    return this.HasAnyEditDrafts() || this.HasAnyTombstones();
  }

  GetTombstoneCount() {
    return Object.keys(this.fTombstones).length;
  }

  GetEditDraftSetBuckets() {
    var buckets = new Map();
    for (var entry of this.GetAllEditDrafts()) {
      var curLayout = this.GetLayoutByID(entry.id, false, enumBoxStyleSearch.allSets);
      if (!curLayout) continue;
      var setId = this.GetBoxStyleSetFromID(entry.id, true);
      if (!buckets.has(setId)) buckets.set(setId, []);
      buckets.get(setId).push({ entry, curLayout });
    }

    // Staged deletes are already spliced out of the in-memory list, but a delete-only set must
    // still get an (empty) bucket here so DoCommitAndExit's commit loop issues a whole-list
    // SaveBoxStyles for the set; that save is what persists the removal.
    for (var tomb of this.GetAllTombstones()) {
      if (!buckets.has(tomb.setId)) buckets.set(tomb.setId, []);
    }

    return buckets;
  }

  InitBoxStylesMgr() {
    this.fSysBoxStyles = [];
    this.fUserBoxStyles = [];
    this.fSharedBoxStyles = [];
    this.fDirty = { 0: false, 1: false, 2: false };
  }

  SwitchBoxStyles(styleSet) {
    if (styleSet == null || Object.values(enumBoxStyleSet).indexOf(parseInt(styleSet)) === -1)
      styleSet = enumBoxStyleSet.systemboxstyles;
    this.fBoxStyleSet = parseInt(styleSet);
  }

  SetPendingScrollTarget(boxStyleID) {
    this.fPendingScrollTarget = boxStyleID;
  }

  SetHierarchyType(hierarchyType) {
    this.fHierarchyType = hierarchyType;
  }

  GetActiveBoxStylesList() {
    return this.GetBoxStyleListFromSet(this.fBoxStyleSet);
  }

  GetHierarchyType() {
    return this.fHierarchyType;
  }

  HasGroupingAccess() {
    var accountInfo = Globals._accountInfo;
    var groupingEnabledByConfig = Globals._uiConfig?.fShowGrouping === true;
    var hasGroupingFeature =
      accountInfo?.GetAccessibleFeature?.(enumFeaturesList.grouping) === true;

    return groupingEnabledByConfig && hasGroupingFeature;
  }

  /**
   * Check if a box style should be visible for the current brand.
   * For isolved: only show system styles whose IDs are in kIsolvedBoxStyleIDs.
   * For all other brands: show all system styles.
   * @param {number} boxStyleID
   * @returns {boolean}
   */
  IsVisibleBoxStyleForBrand(boxStyleID) {
    if (gWhiteLabel === enumBrand.isolved) {
      return kIsolvedBoxStyleIDs.has(boxStyleID);
    }
    return true;
  }

  GetVisibleLayouts() {
    var activeList = this.GetActiveBoxStylesList();
    const filterHierarchyType = this.GetHierarchyType();
    const isSystemSet = this.CurrentBoxStyleSet() === enumBoxStyleSet.systemboxstyles;
    const hasGroupingAccess = this.HasGroupingAccess();

    return activeList.filter(layout => {
      // null hierarchyType means no filtering (show all)
      if (filterHierarchyType !== null) {
        const styleHierType = layout.GetHierarchyType() ?? enumHierarchyTypes.employees;
        if (styleHierType === enumHierarchyTypes.grouping && !hasGroupingAccess) return false;
        if (filterHierarchyType !== styleHierType) return false;
      }
      // Only apply brand filtering to system styles.
      if (!isSystemSet) return true;

      return this.IsVisibleBoxStyleForBrand(layout.GetBoxStyleID());
    });
  }

  /**
   * Returns visible layouts from all sets filtered by the current hierarchy type.
   * Positions: includes position + employee styles.
   * Employees: includes employee + grouping styles.
   * @returns {{ layout: QBoxLayout, boxStyleSet: (typeof enumBoxStyleSet)[keyof typeof enumBoxStyleSet] }[]}
   */
  GetVisibleLayoutsAllSets(filterHierarchyType) {
    var hierarchySet;
    var hasGroupingAccess = this.HasGroupingAccess();
    if (filterHierarchyType === enumHierarchyTypes.positions) {
      hierarchySet = new Set([enumHierarchyTypes.positions, enumHierarchyTypes.employees]);
    } else {
      hierarchySet = hasGroupingAccess
        ? new Set([enumHierarchyTypes.employees, enumHierarchyTypes.grouping])
        : new Set([enumHierarchyTypes.employees]);
    }
    var results = [];
    var sets = [
      { list: this.fSysBoxStyles, key: enumBoxStyleSet.systemboxstyles },
      { list: this.fSharedBoxStyles, key: enumBoxStyleSet.sharedboxstyles },
      { list: this.fUserBoxStyles, key: enumBoxStyleSet.userboxstyles }
    ];

    for (var s = 0; s < sets.length; s++) {
      var isSystemSet = sets[s].key === enumBoxStyleSet.systemboxstyles;
      var list = sets[s].list;

      for (var i = 0; i < list.length; i++) {
        var layout = list[i];
        var styleHierType = layout.GetHierarchyType() ?? enumHierarchyTypes.employees;
        if (!hierarchySet.has(styleHierType)) continue;

        if (isSystemSet) {
          if (!this.IsVisibleBoxStyleForBrand(layout.GetBoxStyleID())) continue;
        }

        results.push({ layout: layout, boxStyleSet: sets[s].key });
      }
    }

    return results;
  }

  GetSystemDefaultLayout() {
    return this.fSysBoxStyles[0];
  }

  GetBoxStyleListFromSet(styleSet) {
    switch (styleSet) {
      case enumBoxStyleSet.userboxstyles:
        return this.fUserBoxStyles;
      case enumBoxStyleSet.sharedboxstyles:
        return this.fSharedBoxStyles;
      default:
        return this.fSysBoxStyles;
    }
  }

  CurrentBoxStyleSet() {
    return this.fBoxStyleSet;
  }

  SetDirty() {
    this.fDirty[this.fBoxStyleSet] = true;
  }

  IsDirty(theSet) {
    //TODO: Move dirtiness concept to indvidual layouts.
    if (theSet) return this.fDirty[theSet];
    return this.fDirty[this.fBoxStyleSet];
  }

  UpdateBoxStylesPanel() {
    var bsPanel = this.GetBoxStylesPanel();
    if (bsPanel) bsPanel.UpdatePanel();
  }

  GetBoxStylesPanel() {
    var boxTabID = "BoxStyles";
    var tabMgr = Globals._wsMgr.fTabMgr;
    if (tabMgr == null) return;
    if (isInViewer() && isInStandardSubmode()) {
      var tabID = tabMgr.GetTopTabID(); // need to clean this at some point
      if (tabID === "interactiveFiltering") return;
      else if (tabID != null && tabID.includes(boxTabID) && tabID !== "interactiveFiltering")
        boxTabID = tabID;
    }
    return tabMgr.GetTabPanelByID(boxTabID);
  }

  ClearDirty() {
    this.fDirty[this.fBoxStyleSet] = false;
    var styles = this.GetBoxStyleListFromSet(this.fBoxStyleSet);
    for (var aBoxIdx in styles) {
      // get box styles
      var theBox = styles[aBoxIdx];
      theBox.fDirty = false;
    }
    this.UpdateBoxStylesPanel();
  }

  GetUniqueID() {
    return this.fBoxStyleSet == enumBoxStyleSet.sharedboxstyles ? this.fUID++ : this.fPUID++;
  }

  AddLayout(aLayout, tryName) {
    var activeList = this.GetActiveBoxStylesList();
    var uniqueName = this.GetUniqueBoxStyleName(tryName);

    var uniqueID = this.GetUniqueID();

    aLayout.SetBoxStyleID(uniqueID);
    aLayout.SetBoxStyleName(uniqueName);
    activeList.push(aLayout);
    this.SetDirty();

    return aLayout;
  }

  RemoveLayout(aLayout) {
    var activeList = this.GetActiveBoxStylesList();
    var index = this.GetIndexOfLayout(aLayout);
    if (index != -1) activeList.splice(index, 1);
    this.SetDirty();
  }

  ShuffleLayout(aLayout, shuffleUp) {
    var activeList = this.GetActiveBoxStylesList();
    var index = this.GetIndexOfLayout(aLayout);
    if (index < 0) return;
    var n = this.GetNumLayouts();
    var newIndex;

    if (shuffleUp) newIndex = index - 1;
    else newIndex = index + 1;

    if (newIndex < 0) return;
    if (newIndex >= n) return;

    var t = activeList[newIndex];
    activeList[newIndex] = aLayout;
    activeList[index] = t;
    this.SetDirty();
  }

  ReplaceLayout(oldLayout, newLayout, updateName) {
    var activeList = this.GetActiveBoxStylesList();
    var index = this.GetIndexOfLayout(oldLayout);
    if (index != -1) {
      var oldLayout = activeList[index];
      var name = oldLayout.GetBoxStyleName();
      var id = oldLayout.GetBoxStyleID();
      if (!updateName) {
        newLayout.SetBoxStyleName(name);
      }
      newLayout.SetBoxStyleID(id);
      activeList[index] = newLayout;
    }
    this.SetDirty();
  }

  GetIndexOfLayout(aLayout) {
    var index = this.GetActiveBoxStylesList().indexOf(aLayout);
    return index;
  }

  GetNumLayouts() {
    return this.GetActiveBoxStylesList().length;
  }

  GetIndexLayout(index) {
    return this.GetActiveBoxStylesList()[index];
  }

  CloneLayout(aLayout, theDoc) {
    var cloneLayout = aLayout.Clone();
    cloneLayout.SetIsBoxStyle(false);
    theDoc.FinishLoadLayout(cloneLayout);
    return cloneLayout;
  }

  GetLayoutByName(layoutName) {
    var n = this.GetNumLayouts();
    for (var i = 0; i < n; i++) {
      var aLayout = this.GetIndexLayout(i);
      var name = aLayout.GetBoxStyleName();
      if (name == layoutName) return aLayout;
    }

    return null;
  }

  DoGetLayoutByID(layoutID) {
    var n = this.GetNumLayouts();
    if (n == 0) return null;

    for (var i = 0; i < n; i++) {
      var aLayout = this.GetIndexLayout(i);
      var id = aLayout.GetBoxStyleID();
      if (id == layoutID) return aLayout;
    }

    return null;
  }

  GetLayoutByID(layoutID, handleNoMatch, searchSet) {
    var matchLayout;
    var curSet = this.CurrentBoxStyleSet();

    if (searchSet == enumBoxStyleSearch.currentSet) matchLayout = this.DoGetLayoutByID(layoutID);
    else {
      this.SwitchBoxStyles(enumBoxStyleSet.systemboxstyles);
      matchLayout = this.DoGetLayoutByID(layoutID);
      if (!matchLayout) {
        this.SwitchBoxStyles(enumBoxStyleSet.sharedboxstyles);
        matchLayout = this.DoGetLayoutByID(layoutID);
      }
      if (!matchLayout) {
        this.SwitchBoxStyles(enumBoxStyleSet.userboxstyles);
        matchLayout = this.DoGetLayoutByID(layoutID);
      }
    }

    if (handleNoMatch && matchLayout == null) {
      this.SwitchBoxStyles(enumBoxStyleSet.systemboxstyles);
      matchLayout = this.GetIndexLayout(0);
    }

    this.SwitchBoxStyles(curSet);
    return matchLayout;
  }

  GetBoxStyleNameFromID(layoutID, handleNoMatch) {
    var l = this.GetLayoutByID(layoutID, handleNoMatch, enumBoxStyleSearch.allSets);
    if (l != null) return l.GetBoxStyleName();

    return appstr("notset");
  }

  GetBoxStyleSetFromID(layoutID, enumeratedValue) {
    var matchLayout;
    var curSet = this.CurrentBoxStyleSet();

    this.SwitchBoxStyles(enumBoxStyleSet.systemboxstyles);
    matchLayout = this.DoGetLayoutByID(layoutID);
    if (matchLayout) {
      this.SwitchBoxStyles(curSet);
      return enumeratedValue ? enumBoxStyleSet.systemboxstyles : appstr("sysstyles");
    } else {
      this.SwitchBoxStyles(enumBoxStyleSet.sharedboxstyles);
      matchLayout = this.DoGetLayoutByID(layoutID);
      if (matchLayout) {
        this.SwitchBoxStyles(curSet);
        return enumeratedValue ? enumBoxStyleSet.sharedboxstyles : appstr("sharedstyles");
      } else {
        this.SwitchBoxStyles(enumBoxStyleSet.userboxstyles);
        matchLayout = this.DoGetLayoutByID(layoutID);
        if (matchLayout) {
          this.SwitchBoxStyles(curSet);
          return enumeratedValue ? enumBoxStyleSet.userboxstyles : appstr("userstyles");
        }
      }
    }

    this.SwitchBoxStyles(curSet);
    if (layoutID >= 5000)
      return enumeratedValue ? enumBoxStyleSet.sharedboxstyles : appstr("sharedstyles");
    return enumeratedValue ? enumBoxStyleSet.systemboxstyles : appstr("sysstyles");
  }

  IsLastLayout(aLayout) {
    const visibleLayouts = this.GetVisibleLayouts();
    var index = visibleLayouts.indexOf(aLayout);
    if (index != -1 && index == visibleLayouts.length - 1) return true;

    return false;
  }

  IsFirstLayout(aLayout) {
    const visibleLayouts = this.GetVisibleLayouts();
    var index = visibleLayouts.indexOf(aLayout);
    if (index == 0) return true;

    return false;
  }

  BoxStylesSaved() {
    this.ClearDirty();
  }

  BoxStylesDirty(theSet) {
    return this.IsDirty(theSet);
  }

  SaveToJSON(layoutList) {
    //TODO: UID
    var JSONLIST = [];
    var n = layoutList.length;
    for (var i = 0; i < n; i++) {
      var aLayout = layoutList[i];
      var layoutJSON = aLayout.SaveToJSON();
      if (layoutList == this.fSharedBoxStyles) layoutJSON["library"] = "shared";
      else layoutJSON["library"] = "user";
      JSONLIST.push(JSON.parse(JSON.stringify(layoutJSON)));
    }
    return JSONLIST;
  }

  LoadBoxStyleSet(stylesData, theList, loadUID) {
    if (loadUID == 1) this.fUID = stylesData["UID"];
    else if (loadUID == 2) {
      this.fPUID = stylesData["UID"];
    }

    var boxList = stylesData["styles"];
    if (!boxList) return;
    var numLayouts = boxList.length;
    for (var i = 0; i < numLayouts; i++) {
      var layoutInfo = boxList[i];
      var aLayout = new QBoxLayout();
      aLayout.LoadFromJSON(layoutInfo);
      aLayout.FinishLoadBoxStyle();
      theList.push(aLayout);
    }
    //fallback measure to prevent duplicate IDS. - SUPPRESSED as of 5.0.1
    /*
    if (loadUID > 0 && theList.length > 0)
    {
      var minBoxStyleValue = loadUID == 1? kStartUID : kStartPUID;
      var boxIDs = theList.map(function(a) {return a.fBoxStyleID});

      for (var aLayout in theList)
      {
        var theLayout = theList[aLayout];
        while (boxIDs.indexOf(theLayout.fBoxStyleID) != parseInt(aLayout) || theLayout.fBoxStyleID <= minBoxStyleValue)
        {
          if (theLayout.fBoxStyleID <= minBoxStyleValue)
            theLayout.fBoxStyleID = minBoxStyleValue;
          theLayout.fBoxStyleID ++;
          boxIDs = theList.map(function(a) {return a.fBoxStyleID});
        }
      }
      var newMinBSValue = boxIDs.reduce(function(a, b) {
          return Math.max(a, b)});
      if (loadUID == 1)
      {
        if (this.fUID != newMinBSValue + 1)
        {
          this.fUID = newMinBSValue+1;
          this.fDirty[1] = true;
        }
      }
      else
      {
        if (this.fPUID != newMinBSValue + 1)
        {
          this.fPUID = newMinBSValue+1;
          this.fDirty[2] = true;
        }
      }
    }
     */
  }

  LoadFromJSON(theData) {
    if (theData["system"]) {
      this.fSysBoxStyles = [];
      this.LoadBoxStyleSet(theData["system"], this.fSysBoxStyles, 0);
    }
    if (theData["shared"]) {
      this.fSharedBoxStyles = [];
      this.LoadBoxStyleSet(theData["shared"], this.fSharedBoxStyles, 1);
    }
    if (theData["user"]) {
      this.fUserBoxStyles = [];
      this.LoadBoxStyleSet(theData["user"], this.fUserBoxStyles, 2);
    }
  }

  GetBoxStyleNameListAllSets(includeMRB) {
    var priorSet = this.fBoxStyleSet;
    var setList = [
      enumBoxStyleSet.systemboxstyles,
      enumBoxStyleSet.sharedboxstyles,
      enumBoxStyleSet.userboxstyles
    ];
    var nameList = [];
    for (var aSet in setList) {
      var theSet = setList[aSet];
      this.SwitchBoxStyles(theSet);
      var thisList = this.GetBoxStyleNameList(includeMRB);
      nameList = nameList.concat(thisList);
    }
    this.SwitchBoxStyles(priorSet);
    return nameList;
  }

  GetBoxStyleNameList(includeMRB, theSet) {
    var nameList = [];

    var theList = [];
    switch (theSet) {
      case enumBoxStyleSet.systemboxstyles:
        theList = this.fSysBoxStyles;
        break;
      case enumBoxStyleSet.sharedboxstyles:
        theList = this.fSharedBoxStyles;
        break;
      case enumBoxStyleSet.userboxstyles:
        theList = this.fUserBoxStyles;
        break;
      default:
        theList = this.GetActiveBoxStylesList();
        break;
    }
    var n = theList.length;
    for (var i = 0; i < n; i++) {
      var aLayout = theList[i];
      var name = aLayout.GetBoxStyleName();
      if (!includeMRB && aLayout.GetContainerType() != enumContainerType.nonmrb) continue;
      nameList.push(name);
    }
    return nameList;
  }

  GetBoxStyleIDFromNameAllSets(boxStyleName, curSet = null) {
    var priorSet = this.fBoxStyleSet;
    var setList = [
      enumBoxStyleSet.systemboxstyles,
      enumBoxStyleSet.sharedboxstyles,
      enumBoxStyleSet.userboxstyles
    ];
    if (curSet != null) setList = [curSet];
    for (var aSet in setList) {
      var theSet = setList[aSet];
      this.SwitchBoxStyles(theSet);
      var n = this.GetNumLayouts();
      for (var i = 0; i < n; i++) {
        var aLayout = this.GetIndexLayout(i);
        if (aLayout.GetBoxStyleName() == boxStyleName) {
          this.SwitchBoxStyles(priorSet);
          return aLayout.GetBoxStyleID();
        }
      }
    }

    this.SwitchBoxStyles(priorSet);
    return null;
  }

  IsUniqueBoxStyleName(aName, excludeLayout) {
    var n = this.GetNumLayouts();
    for (var i = 0; i < n; i++) {
      var aLayout = this.GetIndexLayout(i);
      if (aLayout != excludeLayout) {
        var name = aLayout.GetBoxStyleName();
        if (name == aName) return false;
      }
    }

    return true;
  }

  GetUniqueBoxStyleName(theName = appstr("Untitled")) {
    var nameCounter = 1;
    var tryName = theName;
    while (!this.IsUniqueBoxStyleName(tryName, null)) {
      tryName = theName + " " + nameCounter.toString();
      nameCounter++;
    }
    return tryName;
  }

  GetMRBBoxStyleNameList(allSets) {
    var priorSet = this.fBoxStyleSet;
    var setList = allSets
      ? [this.fBoxStyleSet]
      : [
          enumBoxStyleSet.systemboxstyles,
          enumBoxStyleSet.sharedboxstyles,
          enumBoxStyleSet.userboxstyles
        ];
    var nameList = [];

    for (var aSet in setList) {
      var theSet = setList[aSet];
      this.SwitchBoxStyles(theSet);
      var n = this.GetNumLayouts();
      for (var i = 0; i < n; i++) {
        var aLayout = this.GetIndexLayout(i);
        if (
          aLayout.GetContainerType() == enumContainerType.standardmrb ||
          aLayout.GetContainerType() == enumContainerType.comgr
        )
          nameList.push(aLayout.GetBoxStyleName());
      }
    }

    this.SwitchBoxStyles(priorSet);
    return nameList;
  }

  // Re-inserts tombstoned (staged-deleted) styles into their owning sets on discard. Mirrors
  // RemoveDraftIfCancelled (re-adds layouts; teardown's ClearAllTombstones wipes the registry).
  RestoreTombstonedDeletes() {
    var priorSet = this.CurrentBoxStyleSet();
    this.GetAllTombstones().forEach(({ layout, setId }) => {
      this.SwitchBoxStyles(setId);
      this.GetActiveBoxStylesList().push(layout);
    });
    this.SwitchBoxStyles(priorSet);
  }

  RemoveDraftIfCancelled(wasEditingBoxStyle, userCancelled) {
    if (!wasEditingBoxStyle || !userCancelled) return;
    var entries = Object.entries(this.fEditDrafts).filter(([, d]) => d.isNew);
    if (entries.length === 0) return;
    // RemoveLayout walks only the active set, so switch into each draft's owning set before removing.
    var priorSet = this.CurrentBoxStyleSet();
    entries.forEach(([key]) => {
      var id = Number(key);
      var layout = this.GetLayoutByID(id, false, enumBoxStyleSearch.allSets);
      if (!layout) return;
      var setId = this.GetBoxStyleSetFromID(id, true);
      this.SwitchBoxStyles(setId);
      this.RemoveLayout(layout);
    });
    this.SwitchBoxStyles(priorSet);
  }
}
